import React, { useState } from 'react';
import { 
  FiUsers, 
  FiDollarSign, 
  FiStar,
  FiArrowUp,
  FiArrowDown
} from 'react-icons/fi';
import MemberGrowthChart from './MemberGrowthChart';
import LoanFunnelChart from './LoanFunnelChart';
import LoanPortfolioChart from './LoanPortfolioChart';
import MemberAcquisition from './MemberAcquisition';
import LoanPerformance from './LoanPerformance';
import UnderservedMarket from './UnderservedMarket';
import FinancialHealth from './FinancialHealth';

interface MetricCardProps {
  title: string;
  value: string | number;
  change: string;
  target: string;
  icon: React.ReactNode;
  isPositive: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({ 
  title, 
  value, 
  change, 
  target, 
  icon, 
  isPositive 
}) => {
  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
      <div className="flex items-start justify-between">
        <div className="flex-grow">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-slate-600 font-medium text-xs sm:text-sm">{title}</h3>
            <div className="bg-slate-100 p-1.5 sm:p-2 rounded-full">
              {icon}
            </div>
          </div>
          
          <div className="mb-3">
            <p className="text-2xl sm:text-3xl font-bold text-slate-800 mb-1">{value}</p>
            <div className="flex items-center space-x-2">
              <span className={`flex items-center text-xs sm:text-sm font-medium ${
                isPositive ? 'text-green-600' : 'text-red-600'
              }`}>
                {isPositive ? (
                  <FiArrowUp className="mr-1" size={12} />
                ) : (
                  <FiArrowDown className="mr-1" size={12} />
                )}
                {change}
              </span>
            </div>
          </div>
          
          <div className="pt-3 border-t border-slate-200">
            <p className="text-slate-500 text-xs sm:text-sm">
              Target: <span className="font-medium text-slate-700">{target}</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

const DashboardOverview: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  
  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'member-acquisition', label: 'Member Acquisition' },
    { id: 'loan-performance', label: 'Loan Performance' },
    { id: 'underserved-market', label: 'Underserved Market' },
    { id: 'financial-health', label: 'Financial Health' }
  ];

  const metrics = [
    {
      title: 'Total Members',
      value: '13,680',
      change: '+8.5%',
      target: '15,000',
      icon: <FiUsers className="text-slate-600" size={20} />,
      isPositive: true
    },
    {
      title: 'Revenue per Member',
      value: '$606',
      change: '+3.2%',
      target: '$650',
      icon: <FiDollarSign className="text-slate-600" size={20} />,
      isPositive: true
    },
    {
      title: 'Member Satisfaction',
      value: '4.6/5.0',
      change: '+0.3%',
      target: '4.8/5.0',
      icon: <FiStar className="text-slate-600" size={20} />,
      isPositive: true
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            {/* Key Metrics Grid */}
            <div>
              <h3 className="text-base sm:text-lg font-medium text-slate-700 mb-4">High-level performance metrics across all key areas</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {metrics.map((metric, index) => (
                  <MetricCard key={index} {...metric} />
                ))}
              </div>
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {/* Member Growth & Revenue Chart */}
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
                <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Member Growth & Revenue</h4>
                <div className="h-64 sm:h-80">
                  <MemberGrowthChart />
                </div>
              </div>

              {/* Loan Application Funnel */}
              <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
                <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Loan Application Funnel</h4>
                <LoanFunnelChart />
              </div>
            </div>

            {/* Loan Portfolio Distribution */}
            <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 border border-slate-200">
              <h4 className="text-base sm:text-lg font-medium text-slate-700 mb-4">Loan Portfolio Distribution</h4>
              <LoanPortfolioChart />
            </div>
          </div>
        );
      case 'member-acquisition':
        return <MemberAcquisition />;
      case 'loan-performance':
        return <LoanPerformance />;
      case 'underserved-market':
        return <UnderservedMarket />;
      case 'financial-health':
        return <FinancialHealth />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Navigation Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex overflow-x-auto scrollbar-hide space-x-2 sm:space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-3 px-3 sm:px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex-shrink-0 ${
                activeTab === tab.id
                  ? 'border-slate-600 text-slate-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
};

export default DashboardOverview;